import { useMemo } from 'react';
import { IStore } from '../../interfaces';
import { IAvailableCompetition, ISelectedEvolutions, ITeam } from '../../interfaces/clientInterfaces';
import { useSelector } from 'react-redux';
import * as actions from '../../actions';
import { useAppDispatch } from '../../utils/modules';
import styles from './Participations.module.css';

interface ITeamBar {
	name: string;
	teamCode: string;
	regionCode: string;
	seasonsOnTop: number;
	ranking: number;
	isInTopDivision: boolean;
	isDissolved: boolean;
	id: number;
}

interface IParticipationsInjectedProps {
	teams: ITeam[];
	availableCompetitions: IAvailableCompetition[];
	selectedEvolutions: ISelectedEvolutions[];
	isMobileViewActive: boolean;
}

export default function Participations() {

	const dispatch = useAppDispatch();
	
	const {
		teams,
		availableCompetitions,
		selectedEvolutions,
		isMobileViewActive,
	} = useSelector(
		(completeState: IStore): IParticipationsInjectedProps => {
			return {
				teams: completeState.teams.teams,
				availableCompetitions: completeState.competitions.availableCompetitions,
				selectedEvolutions: completeState.competitions.selectedEvolutions,
				isMobileViewActive: completeState.ui.isMobileViewActive
			}
		});

	const { orderedTeams, totalParticipations, hasReserveTeams } = useMemo(
		(): {
			orderedTeams: ITeamBar[];
			totalParticipations: number;
			hasReserveTeams: boolean;
		 } => {
			if (selectedEvolutions.length < 1) {
				return {
					orderedTeams: [],
					totalParticipations: 0,
					hasReserveTeams: false
				};
			}
			const competitionName = selectedEvolutions[0].competitionName;
			const competition = availableCompetitions.find(competition => competition.name === competitionName);
			if (!competition) {
				return {
					orderedTeams: [],
					totalParticipations: 0,
					hasReserveTeams: false
				};
			}
			const filteredTeams: ITeamBar[] = [];
			for (const team of teams) {
				if (competition.regionCode === 'DR' || competition.regionCode === 'UR' || competition.regionCode === 'ZK' || competition.regionCode === 'YU') {
					if (team.countryCode2 === competition.regionCode && team.seasonsTop2 && team.seasonsTop2 > 0) {
						filteredTeams.push({
							name: team.name,
							teamCode: `${team.countryCode}_${team.teamCode}`,
							regionCode: competition.regionCode === 'DR' ? '' : team.countryCode,
							seasonsOnTop: team.seasonsTop2,
							ranking: team.overallRanking,
							isInTopDivision: false,
							isDissolved: team.dissolved !== null,
							id: team.teamId
						});
					}
				} else {
					if (team.countryCode === competition.regionCode) {
						if (competition.competitionCode.indexOf('L2') > -1) {
							if (team.seasons2nDiv && team.seasons2nDiv > 0) {
								filteredTeams.push({
									name: team.name,
									teamCode: `${team.countryCode}_${team.teamCode}`,
									regionCode: team.countryCode2 === 'MC' ? 'MC' : '',
									seasonsOnTop: team.seasons2nDiv,
									ranking: team.overallRanking,
									isInTopDivision: team.category === 2,
									isDissolved: team.dissolved !== null,
									id: team.teamId
								});
							}
						} else if (team.seasonsTop && team.seasonsTop > 0) {
							filteredTeams.push({
								name: team.name,
								teamCode: `${team.countryCode}_${team.teamCode}`,
								regionCode: team.countryCode2 === 'MC' ? 'MC' : '',
								seasonsOnTop: team.seasonsTop,
								ranking: team.overallRanking,
								isInTopDivision: team.category === 1,
								isDissolved: team.dissolved !== null,
								id: team.teamId
							});
						} 
					} else if (team.countryCode2 === competition.regionCode) {
						if (competition.competitionCode.indexOf('L2') > -1) {
							if (team.seasons2nDiv && team.seasons2nDiv > 0) {
								filteredTeams.push({
									name: team.name,
									teamCode: `${team.countryCode}_${team.teamCode}`,
									regionCode: team.countryCode,
									seasonsOnTop: team.seasons2nDiv,
									ranking: team.overallRanking,
									isInTopDivision: team.countryCode === 'WL' || team.countryCode === 'NI' ? team.category === 2 : false,
									isDissolved: team.dissolved !== null,
									id: team.teamId
								});
							}
						} else if (team.seasonsTop2 && team.seasonsTop2 > 0) {
							filteredTeams.push({
								name: team.name,
								teamCode: `${team.countryCode}_${team.teamCode}`,
								regionCode: team.countryCode,
								seasonsOnTop: team.seasonsTop2,
								ranking: team.overallRanking,
								isInTopDivision: team.countryCode === 'WL' || team.countryCode === 'NI' ? team.category === 1 : false,
								isDissolved: team.dissolved !== null,
								id: team.teamId
							});
						}
					}
				}
			}
			const orderedTeams = filteredTeams.sort((a, b) => {
				return a.seasonsOnTop > b.seasonsOnTop ? -1 : a.seasonsOnTop < b.seasonsOnTop ? 1 : a.ranking >= b.ranking ? -1 : 1;
			});
			return {
				orderedTeams,
				totalParticipations: competition.totalEditions ?? 0,
				hasReserveTeams: orderedTeams.some(team => team.name.indexOf('*') > -1)
			};
		},
		[selectedEvolutions, availableCompetitions, teams]
	);

	return (
		<div className={isMobileViewActive ? styles.mobileParticipationsContainer : styles.participationsContainer}>
			<div className={styles.totalSeasonsContainer}>
				<div className={styles.legendContainer}>
					<div className={styles.legendContainerTop}>{'Currently in league'}</div>
					<div className={styles.legendContainerDissolved}>{'Team dissolved'}</div>
					{
						hasReserveTeams && (
							<div className={styles.reserveLegendContainer}>{'RESERVE TEAM*'}</div>
						)
					}
				</div>
				{
					totalParticipations > 0 && (
						<div className={styles.totalNameContainer}>{`Total Seasons: ${totalParticipations}`}</div>
					)
				}
				
			</div>
			{
				orderedTeams.map(team => {
					const width = 100 * team.seasonsOnTop / totalParticipations;
					return (
						<div
							className={styles.teamTimeHonourContainer}
							onClick={e => dispatch(actions.setHomeDetailTeam(team.id))}
						>
							<div
								className={isMobileViewActive ? styles.mobileTeamTimeHonourNameAndImage : styles.teamTimeHonourNameAndImage} 
							>
								<img
									alt={team.name}
									className={styles.teamScarfImage}
									style={{
										maxWidth: isMobileViewActive ? '120px' : '160px'
									}}
									src={`./../../img/scarf/${team.teamCode}.jpg`}
								/>
							</div>
							<div className={styles.participationsTitlesBarContainer}>
								<div
									className={
										team.isInTopDivision
											? styles.topDivisionContentBar
											: team.isDissolved
												? styles.dissolvedContentBar
												: styles.teamContentBar
									}
								>
									{team.regionCode
										? <img
											alt={team.regionCode}
											className={styles.participationsRegionImage}
											src={`./../../img/flag/${team.regionCode}.png`}
										/>
										: null	
									}
									<img
										alt={team.name}
										src={`./../../img/shield/${team.teamCode}.png`}
										title={team.name}
										style={{
											maxHeight: isMobileViewActive ? 25 : 30,
											margin: `auto ${!!team.regionCode ? '0' : '3px'}`
										}}
									/>
									<div
										className={styles.participationsTeamNameContainer}
									>
										{team.name}
									</div>
									<div
										className={styles.participationsTitlesNumberContainer}
									>
										{`${team.seasonsOnTop} ${isMobileViewActive ? '' : 'seasons'}`}
									</div>
								</div>
								<div
									className={styles.participationsBarContainer}
									style={{
										width: `${width}%`
									}}
								/>
							</div>
						</div>
					);
				})
			}
		</div>
	);
}
